import React, { useState } from 'react';
import { Horse } from './types';
import { matchContractDocument, changeOwner, saveContract, saveHorseList, fetchHorseList } from './api';
import './ContractMatchReview.css';

interface MatchCandidate {
  horse_id: string;
  horse_name: string;
  contract_id: string | null;
  owner_name: string | null;
  start_date: string | null;
  score: number;
}

interface Props {
  documentId: number;
  extractedData: Record<string, any>;
  matches: MatchCandidate[];
  horses: Horse[];
  onClose: () => void;
  onMatched: () => void;
}

const ContractMatchReview: React.FC<Props> = ({
  documentId, extractedData, matches, horses, onClose, onMatched
}) => {
  const [selectedHorseId, setSelectedHorseId] = useState<string>(
    matches.length > 0 ? matches[0].horse_id : ''
  );
  const [saving, setSaving] = useState(false);
  const [newHorseName, setNewHorseName] = useState(extractedData.horse_name || '');

  // Check if selected horse has a different owner than extracted data
  const getOwnerMismatch = (): { currentOwner: string; extractedOwner: string } | null => {
    if (!selectedHorseId || !extractedData.owner_name) return null;
    const horse = horses.find(h => h.id === selectedHorseId);
    const match = matches.find(m => m.horse_id === selectedHorseId);
    const currentOwner = horse?.contract?.owner_name || match?.owner_name || '';
    if (!currentOwner) return null;
    // Simple comparison: ignore case and trim
    if (currentOwner.trim().toLowerCase() === extractedData.owner_name.trim().toLowerCase()) return null;
    return { currentOwner, extractedOwner: extractedData.owner_name };
  };

  const ownerMismatch = getOwnerMismatch();

  const handleConfirm = async () => {
    if (!selectedHorseId) return;
    setSaving(true);
    try {
      const horse = horses.find(h => h.id === selectedHorseId);
      const contractId = horse?.contract?.id || matches.find(m => m.horse_id === selectedHorseId)?.contract_id;
      await matchContractDocument(documentId, selectedHorseId, contractId || undefined);
      onMatched();
      onClose();
    } catch (error: any) {
      alert(`Fehler: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleOwnerChange = async () => {
    if (!selectedHorseId || !ownerMismatch) return;

    const changeDate = extractedData.start_date || extractedData.contract_date || new Date().toISOString().split('T')[0];

    if (!window.confirm(
      `Besitzerwechsel durchführen?\n\n` +
      `Alter Besitzer: ${ownerMismatch.currentOwner}\n` +
      `Neuer Besitzer: ${ownerMismatch.extractedOwner}\n` +
      `Wechseldatum: ${new Date(changeDate).toLocaleDateString('de-DE')}\n\n` +
      `Der alte Vertrag wird beendet und ein neuer Vertrag angelegt.`
    )) return;

    setSaving(true);
    try {
      const result = await changeOwner(selectedHorseId, changeDate, {
        owner_name: extractedData.owner_name,
        owner_street: extractedData.owner_street,
        owner_city: extractedData.owner_city,
        box_rent: extractedData.box_rent ? parseFloat(extractedData.box_rent) : undefined,
      });

      // Link document to new contract
      await matchContractDocument(documentId, selectedHorseId, result.new_contract_id);
      onMatched();
      onClose();
    } catch (error: any) {
      alert(`Fehler beim Besitzerwechsel: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Validate a date string — returns it if valid YYYY-MM-DD, otherwise fallback
  const validDate = (d: any, fallback: string): string => {
    if (!d || typeof d !== 'string') return fallback;
    const parsed = new Date(d);
    if (isNaN(parsed.getTime())) return fallback;
    // Verify round-trip: re-format and check it matches a real date
    const [y, m, day] = d.split('-').map(Number);
    if (!y || !m || !day || m < 1 || m > 12 || day < 1 || day > 31) return fallback;
    return d;
  };

  const handleCreateHorse = async () => {
    const horseName = newHorseName.trim();
    if (!horseName) {
      alert('Bitte Pferdenamen eingeben.');
      return;
    }

    const today = new Date().toISOString().split('T')[0];
    const startDate = validDate(extractedData.start_date, today);
    const contractDate = validDate(extractedData.contract_date, startDate);
    const endDate = validDate(extractedData.end_date, '');

    if (!window.confirm(
      `Neues Pferd anlegen?\n\n` +
      `Pferd: ${horseName}\n` +
      `Besitzer: ${extractedData.owner_name || '-'}\n` +
      `Boxenmiete: ${extractedData.box_rent || '-'} €\n` +
      `Mietbeginn: ${new Date(startDate).toLocaleDateString('de-DE')}`
    )) return;

    setSaving(true);
    try {
      // Get current horse list for nextId
      const horseListData = await fetchHorseList();
      const nextId = horseListData.ponyNextID;
      const horseId = `pony/${nextId}`;

      // Create horse
      const newHorse: Record<string, any> = {
        id: horseId,
        name: horseName,
        owner: extractedData.owner_name || '',
        phone: '',
        weight: 0,
        hidden: false,
        early: 'nichts',
        late: 'nichts',
        bricks: 'keine',
      };

      const updatedPonys = { ...horseListData.ponys, [horseId]: newHorse };
      await saveHorseList({ ponyNextID: nextId + 1, ponys: updatedPonys });

      // Create contract
      const isLargeBox = extractedData.box_size?.includes('3,50') || extractedData.box_size?.includes('3.50') || extractedData.box_size?.includes('3,5');
      const contractResult = await saveContract({
        horse_id: horseId,
        owner_name: extractedData.owner_name || '',
        owner_street: extractedData.owner_street || '',
        owner_city: extractedData.owner_city || '',
        owner_phone: '',
        box_rent: extractedData.box_rent ? parseFloat(extractedData.box_rent) : 0,
        rent_surcharge: 0,
        contract_date: contractDate,
        start_date: startDate,
        end_date: endDate || null,
        indefinite: extractedData.indefinite !== false,
        large_box: isLargeBox || false,
        hidden: false,
        active: true,
      });

      // Link document
      await matchContractDocument(documentId, horseId, contractResult.contract_id);
      onMatched();
      onClose();
    } catch (error: any) {
      alert(`Fehler beim Anlegen: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleNoMatch = async () => {
    setSaving(true);
    try {
      await matchContractDocument(documentId, undefined, undefined, 'unmatch');
      onMatched();
      onClose();
    } catch (error: any) {
      alert(`Fehler: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d: string | null): string => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('de-DE'); } catch { return d; }
  };

  const fields = [
    { key: 'horse_name', label: 'Pferdename' },
    { key: 'owner_name', label: 'Besitzer' },
    { key: 'owner_street', label: 'Straße' },
    { key: 'owner_city', label: 'PLZ / Ort' },
    { key: 'start_date', label: 'Mietbeginn' },
    { key: 'end_date', label: 'Mietende' },
    { key: 'box_rent', label: 'Boxenmiete' },
    { key: 'box_size', label: 'Boxgröße' },
    { key: 'contract_date', label: 'Vertragsdatum' },
  ];

  return (
    <>
      <div className="match-overlay" onClick={onClose}></div>
      <div className="match-dialog">
        <h3>Vertragsanalyse — Zuordnung</h3>

        <div className="match-content">
          <div className="match-column">
            <h4>Extrahierte Daten</h4>
            <div className="extracted-fields">
              {fields.map(f => {
                const value = extractedData[f.key];
                return (
                  <div key={f.key} className="extracted-field">
                    <label>{f.label}:</label>
                    <span>{value != null ? String(value) : '-'}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="match-column">
            <h4>Vorgeschlagene Zuordnung</h4>
            {matches.length > 0 ? (
              <div className="match-candidates">
                {matches.map((m, i) => (
                  <label
                    key={m.horse_id + '-' + i}
                    className={`match-candidate ${selectedHorseId === m.horse_id ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="match"
                      checked={selectedHorseId === m.horse_id}
                      onChange={() => setSelectedHorseId(m.horse_id)}
                    />
                    <div className="candidate-info">
                      <div className="candidate-name">{m.horse_name}</div>
                      <div className="candidate-details">
                        {m.owner_name && <span>Besitzer: {m.owner_name}</span>}
                        {m.start_date && <span> | Seit: {formatDate(m.start_date)}</span>}
                      </div>
                      <div className="candidate-score">
                        Übereinstimmung: {m.score}%
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <p className="no-matches">Keine automatischen Treffer gefunden.</p>
            )}

            {ownerMismatch && (
              <div className="owner-mismatch-warning">
                <strong>Besitzer abweichend:</strong> Im System steht „{ownerMismatch.currentOwner}", im Vertrag „{ownerMismatch.extractedOwner}".
              </div>
            )}

            <div className="manual-select">
              <label>Oder manuell zuordnen:</label>
              <select
                value={selectedHorseId}
                onChange={(e) => setSelectedHorseId(e.target.value)}
              >
                <option value="">-- Pferd wählen --</option>
                {horses
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(h => (
                    <option key={h.id} value={h.id}>
                      {h.name} {h.owner ? `(${h.owner})` : ''}
                    </option>
                  ))}
              </select>
            </div>
          </div>
        </div>

        <div className="create-horse-section">
          <label>Pferd neu anlegen:</label>
          <div className="create-horse-row">
            <input
              type="text"
              value={newHorseName}
              onChange={(e) => setNewHorseName(e.target.value)}
              placeholder="Pferdename eingeben"
              className="input-horse-name"
            />
            <button
              onClick={handleCreateHorse}
              className="btn-create-horse"
              disabled={saving || !newHorseName.trim()}
            >
              Anlegen
            </button>
          </div>
        </div>

        <div className="match-actions">
          {ownerMismatch && (
            <button
              onClick={handleOwnerChange}
              className="btn-owner-change"
              disabled={saving}
            >
              Besitzerwechsel
            </button>
          )}
          <button
            onClick={handleConfirm}
            className="btn-confirm-match"
            disabled={!selectedHorseId || saving}
          >
            Zuordnung bestätigen
          </button>
          <button
            onClick={handleNoMatch}
            className="btn-no-match"
            disabled={saving}
          >
            Kein Treffer
          </button>
          <button onClick={onClose} className="btn-cancel-match">
            Abbrechen
          </button>
        </div>
      </div>
    </>
  );
};

export default ContractMatchReview;
